import { clsx } from 'clsx';
import { X } from 'lucide-react';
import React, { useEffect } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full h-full',
  };

  return (
    <div className="fixed inset-0 z-50 modal-container">
      <div
        className={clsx(
          'flex h-screen',
          size === 'full' ? 'items-start' : 'items-center justify-center p-4'
        )}
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div
          className={clsx(
            'relative bg-white dark:bg-graphite-800 shadow-xl w-full',
            size === 'full' ? 'h-full' : 'rounded-lg',
            sizeClasses[size]
          )}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-3 border-b border-light-200 dark:border-graphite-700">
              {title && (
                <h3 className="text-base font-semibold text-deepTechBlue-500 dark:text-light-50">
                  {title}
                </h3>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="text-deepTechBlue-500 dark:text-light-500 hover:text-deepTechBlue-600 dark:hover:text-light-300 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div
            className={clsx(
              size === 'full' 
                ? 'h-full flex flex-col' 
                : 'p-6 max-h-[calc(100vh-150px)] overflow-hidden flex flex-col'
            )}
          >
            {size === 'full' ? (
              <div className="flex-1 overflow-hidden">{children}</div>
            ) : (
              <div className="flex-1 overflow-y-auto">{children}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
